import { GUEST_FIELDS, type DeskGuest, type GuestAccess, type GuestField, type GuestFieldMode, type GuestFields } from '@deskworks/shared-types';
import { useQuery } from '@tanstack/react-query';
import { Download, Trash2, UserRound, Users } from 'lucide-react';
import { useEffect, useState } from 'react';
import { RelativeTime } from '@/components/relative-time';
import { EmptyState, ErrorState, LoadingRows } from '@/components/states';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { api } from '@/lib/api';
import { useApiMutation } from '@/lib/hooks';
import { t } from '@/lib/i18n';
import { ACCESS_TEXT } from '@/routes/shared-desk';

const peopleKey = (accountId: string) => ['account', accountId, 'people'] as const;

const FIELD_LABEL: Record<GuestField, string> = {
  firstName: t('First name'),
  lastName: t('Last name'),
  company: t('Company'),
  email: t('Email'),
  phone: t('Phone'),
};

const MODE_LABEL: Record<GuestFieldMode, string> = { required: t('Required'), optional: t('Optional'), off: t('Not asked') };

const ROLE_LABEL = { owner: t('Owner'), admin: t('Admin'), member: t('Member') };

/**
 * Settings → Account → People (docs/features/desk-sharing.md): the account's members, the guests of its shared desks
 * (what they gave, which desk, what they may) and what the join form asks.
 */
export function AccountPeople({ accountId, admin }: { accountId: string; admin: boolean }) {
  const people = useQuery({ queryKey: peopleKey(accountId), queryFn: () => api.sharing.people(accountId), enabled: admin });
  if (!admin) return <EmptyState compact title={t('Only account admins see the people of the account.')} />;
  if (people.isPending) return <LoadingRows rows={4} />;
  if (people.isError) return <ErrorState error={people.error} onRetry={() => void people.refetch()} />;
  const { members, guests, fields } = people.data;
  return (
    <div className="grid gap-6">
      <Card className="p-0">
        <SectionHead icon={<Users />} title={t('Members ({n})', { n: members.length })} description={t('People of the account: they work in its workspaces.')} />
        <ul className="divide-y border-t">
          {members.map((m) => (
            <li key={m.userId} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-5 py-3 text-sm">
              <span className="font-medium">{m.name}</span>
              <span className="min-w-0 flex-1 truncate text-muted-foreground">{m.email}</span>
              <Badge tone="muted">{ROLE_LABEL[m.role]}</Badge>
            </li>
          ))}
        </ul>
      </Card>
      <Card className="p-0">
        <SectionHead
          icon={<UserRound />}
          title={t('Guests ({n})', { n: guests.length })}
          description={t('People who came by a desk’s link. They see only that desk.')}
          action={
            guests.length > 0 && (
              <Button size="sm" variant="outline" onClick={() => downloadCsv(guests)}>
                <Download /> {t('CSV')}
              </Button>
            )
          }
        />
        {guests.length ? (
          <ul className="divide-y border-t">
            {guests.map((g) => (
              <GuestRow key={g.id} guest={g} accountId={accountId} />
            ))}
          </ul>
        ) : (
          <p className="border-t px-5 py-4 text-sm text-muted-foreground">{t('No guests yet. Share a desk from its page (Share).')}</p>
        )}
      </Card>
      <GuestFieldsCard accountId={accountId} fields={fields} />
    </div>
  );
}

function SectionHead({ icon, title, description, action }: { icon: React.ReactNode; title: string; description: string; action?: React.ReactNode }) {
  return (
    <div className="flex items-start gap-3 px-5 py-4">
      <span className="mt-0.5 text-muted-foreground [&>svg]:size-4">{icon}</span>
      <div className="min-w-0 flex-1">
        <h2 className="text-sm font-semibold">{title}</h2>
        <p className="text-xs text-muted-foreground">{description}</p>
      </div>
      {action}
    </div>
  );
}

function GuestRow({ guest: g, accountId }: { guest: DeskGuest; accountId: string }) {
  const update = useApiMutation((access: GuestAccess) => api.sharing.updateGuest(g.id, { access }), { invalidate: [peopleKey(accountId)] });
  const remove = useApiMutation(() => api.sharing.removeGuest(g.id), {
    invalidate: [peopleKey(accountId)],
    success: t('{name} is out of the desk.', { name: g.name }),
  });
  return (
    <li className="flex flex-wrap items-start gap-x-4 gap-y-2 px-5 py-3 text-sm">
      <div className="min-w-0 flex-1 basis-56">
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="font-medium">{g.name}</span>
          {g.company && <span className="text-muted-foreground">· {g.company}</span>}
          {g.registered && <Badge tone="outline">{t('Account')}</Badge>}
        </div>
        <div className="mt-0.5 flex flex-wrap gap-x-3 text-xs text-muted-foreground">
          {g.email && (
            <a href={`mailto:${g.email}`} className="hover:text-foreground">
              {g.email}
            </a>
          )}
          {g.phone && (
            <a href={`tel:${g.phone.replace(/[^\d+]/g, '')}`} className="hover:text-foreground">
              {g.phone}
            </a>
          )}
        </div>
        <div className="mt-0.5 text-xs text-muted-foreground">
          {g.deskName} · {g.workspaceName} · {t('came')} <RelativeTime value={g.createdAt} />
          {g.lastSeenAt && (
            <>
              {' '}
              · {t('last here')} <RelativeTime value={g.lastSeenAt} />
            </>
          )}
        </div>
      </div>
      <div className="flex items-center gap-1">
        <Select value={g.access} onValueChange={(v) => update.mutate(v as GuestAccess)} disabled={update.isPending}>
          <SelectTrigger size="sm" className="w-40" aria-label={t('Access of {name}', { name: g.name })}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {(['read', 'write'] as const).map((a) => (
              <SelectItem key={a} value={a}>
                {ACCESS_TEXT[a].label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          size="icon-sm"
          variant="ghost"
          aria-label={t('Take {name} out', { name: g.name })}
          title={t('Take out: no way in any more, also not by the link. Their chats stay with the desk.')}
          disabled={remove.isPending}
          onClick={() => window.confirm(t('Take {name} out of “{desk}”?', { name: g.name, desk: g.deskName })) && remove.mutate()}
        >
          <Trash2 />
        </Button>
      </div>
    </li>
  );
}

/** What the join form of every link of the account asks; at least one name stays required. */
function GuestFieldsCard({ accountId, fields }: { accountId: string; fields: GuestFields }) {
  const [draft, setDraft] = useState(fields);
  useEffect(() => setDraft(fields), [fields]);
  const save = useApiMutation((next: GuestFields) => api.sharing.setGuestFields(accountId, next), {
    invalidate: [peopleKey(accountId)],
    success: t('Saved: the form asks this from now on.'),
  });
  const changed = GUEST_FIELDS.some((f) => draft[f] !== fields[f]);
  const nameMissing = draft.firstName !== 'required' && draft.lastName !== 'required';
  return (
    <Card className="p-0">
      <SectionHead icon={<UserRound />} title={t('What guests give')} description={t('The form of every shared desk of this account. With an account, the email is always required.')} />
      <div className="grid gap-3 border-t px-5 py-4 sm:grid-cols-2">
        {GUEST_FIELDS.map((f) => (
          <label key={f} className="flex items-center justify-between gap-3 text-sm">
            <span>{FIELD_LABEL[f]}</span>
            <Select value={draft[f]} onValueChange={(v) => setDraft({ ...draft, [f]: v as GuestFieldMode })}>
              <SelectTrigger size="sm" className="w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(['required', 'optional', 'off'] as const).map((m) => (
                  <SelectItem key={m} value={m}>
                    {MODE_LABEL[m]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </label>
        ))}
      </div>
      <div className="flex flex-wrap items-center justify-end gap-3 border-t px-5 py-3">
        {nameMissing && <span className="text-xs text-amber-700 dark:text-amber-400">{t('At least one name (first or last) must be required.')}</span>}
        <Button size="sm" disabled={!changed || nameMissing} loading={save.isPending} onClick={() => save.mutate(draft)}>
          {t('Save')}
        </Button>
      </div>
    </Card>
  );
}

function downloadCsv(guests: DeskGuest[]) {
  const head = ['First name', 'Last name', 'Company', 'Email', 'Phone', 'Desk', 'Workspace', 'Access', 'Account', 'Came', 'Last here', 'Consent'];
  const cell = (v: string | null | undefined) => {
    const s = v ?? '';
    // Quoted, and a leading formula character neutralised, so a spreadsheet shows it as text.
    return `"${(/^[=+\-@]/.test(s) ? `'${s}` : s).replace(/"/g, '""')}"`;
  };
  const rows = guests.map((g) =>
    [g.firstName, g.lastName, g.company, g.email, g.phone, g.deskName, g.workspaceName, g.access, g.registered ? 'yes' : 'no', g.createdAt, g.lastSeenAt, g.consentAt].map(cell).join(','),
  );
  const blob = new Blob([`﻿${[head.join(','), ...rows].join('\r\n')}`], { type: 'text/csv;charset=utf-8' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `guests-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(a.href);
}
